import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { api } from "@/lib/api";

const ROTATE_MS = 6000;

export const ReviewSpotlight = () => {
  const [quotes, setQuotes] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => {
        const five = (r.data.reviews || []).filter((x) => x.rating === 5 && x.text);
        setQuotes(five.slice(0, 6));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % quotes.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [quotes.length]);

  if (quotes.length === 0) return null;
  const q = quotes[idx];

  return (
    <section data-testid="review-spotlight" className="bg-brand-navy py-16 lg:py-20">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <div className="flex items-center justify-center gap-1.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-5 w-5 fill-fall-amber text-fall-amber" />
          ))}
        </div>
        <div className="relative mt-6 min-h-[9rem] sm:min-h-[7.5rem]">
          <Quote className="absolute -top-2 left-1/2 h-8 w-8 -translate-x-1/2 text-fall-amber/25" aria-hidden />
          <AnimatePresence mode="wait">
            <motion.figure
              key={idx}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <blockquote data-testid="review-spotlight-quote" className="font-display text-lg font-medium leading-relaxed text-white sm:text-xl">
                &ldquo;{q.text.length > 220 ? q.text.slice(0, 220).trimEnd() + "…" : q.text}&rdquo;
              </blockquote>
              <figcaption data-testid="review-spotlight-author" className="mt-4 text-sm font-bold text-fall-amber">
                {q.author?.name || "Google reviewer"}
                <span className="ml-2 font-semibold text-slate-400">· Google review{q.relativeTime ? ` · ${q.relativeTime}` : ""}</span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>
        <div className="mt-5 flex items-center justify-center gap-2">
          {quotes.map((_, i) => (
            <button
              key={i}
              data-testid={`review-spotlight-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Show review ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-fall-amber" : "w-1.5 bg-white/25 hover:bg-white/50"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
